import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { ChatWindow } from "@/components/ChatWindow";
import { ChevronLeft, Loader2, User2, Hash, FileText } from "lucide-react";
import { toast } from "sonner";

export default function AdminChatDetail() {
  const { cid } = useParams();
  const nav = useNavigate();
  const [conv, setConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const lastTs = useRef(null);

  const load = async (initial = false) => {
    try {
      const params = !initial && lastTs.current ? { after: lastTs.current } : {};
      const { data } = await api.get(`/admin/chat/conversations/${cid}/messages`, { params });
      if (initial) setConv(data.conversation);
      if (data.messages.length) {
        lastTs.current = data.messages[data.messages.length - 1].created_at;
        setMessages((prev) => (initial ? data.messages : [...prev, ...data.messages]));
        if (data.messages.some((m) => m.sender_role === "CUSTOMER")) {
          await api.post(`/admin/chat/conversations/${cid}/read`).catch(() => {});
        }
      }
    } finally {
      if (initial) setLoading(false);
    }
  };

  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid]);

  const send = async (payload) => {
    setSending(true);
    try {
      const { data } = await api.post(`/admin/chat/conversations/${cid}/messages`, payload);
      lastTs.current = data.created_at;
      setMessages((p) => [...p, data]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send reply");
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  return (
    <div className="flex flex-col" style={{ height: "calc(100dvh - 6rem)" }} data-testid="admin-chat-detail">
      <button onClick={() => nav("/admin/chats")} className="text-sm text-muted-foreground hover:text-foreground flex items-center mb-3" data-testid="admin-chat-back">
        <ChevronLeft className="h-4 w-4" /> All chats
      </button>

      <Card className="p-4 ip-card-shadow mb-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="h-10 w-10 grid place-items-center rounded-full bg-brand text-white font-bold shrink-0">
            {(conv?.customer_name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold flex items-center gap-1.5 flex-wrap" data-testid="chat-customer-name">
              <User2 className="h-4 w-4 text-muted-foreground" /> {conv?.customer_name}
            </p>
            <p className="text-xs text-muted-foreground truncate">{conv?.customer_email}</p>
          </div>
          <div className="text-xs text-muted-foreground space-y-1 text-right">
            <p className="flex items-center gap-1 justify-end"><Hash className="h-3 w-3" /> ID: <span className="font-mono" data-testid="chat-customer-id">{(conv?.user_id || "").slice(0, 8)}</span></p>
            <p className="flex items-center gap-1 justify-end"><FileText className="h-3 w-3" /> <span className="font-mono" data-testid="chat-customer-reference">{conv?.reference_number || "No application yet"}</span></p>
          </div>
        </div>
      </Card>

      <ChatWindow messages={messages} selfRole="ADMIN" onSend={send} sending={sending} />
    </div>
  );
}
